import { readonly, ref } from 'vue';
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';

// The secret of the open handoff link, kept per browser session like the auth token, so the page
// still has it after the login and after a reload once it has left the address bar.
const STORAGE_KEY = 'mcp_handoff_link';

let inMemory: string | null = null;
const revision = ref(0);

/** Counts installed links, so an open page notices a new one; it holds nothing of the secret. */
export const handoffLinkRevision = readonly(revision);

export function handoffSecretFromHash(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, '')).get('draft')?.trim();
  return value || null;
}

export function getHandoffSecret(): string | null {
  if (inMemory) return inMemory;
  try {
    inMemory = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable (privacy mode). Fall back to memory only.
  }
  return inMemory;
}

export function setHandoffSecret(secret: string | null): void {
  inMemory = secret;
  revision.value++;
  try {
    if (secret) {
      sessionStorage.setItem(STORAGE_KEY, secret);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors; we still keep the in-memory value.
  }
}

/**
 * Takes the secret out of the handoff page's fragment before any other guard sees the route. The
 * redirect replaces the history entry, so the secret leaves the address bar and the history, and
 * the login's return path never contains it.
 */
export function handoffLinkGuard(to: RouteLocationNormalized): RouteLocationRaw | true {
  if (to.name !== 'McpHandoff' || !to.hash) return true;
  const secret = handoffSecretFromHash(to.hash);
  if (secret) setHandoffSecret(secret);
  return { path: to.path, query: to.query, hash: '', replace: true };
}
